import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { 
  Car, LogOut, Calendar as CalendarIcon, Clock, Search, Download, 
  ChevronLeft, ChevronRight, Filter, Users, Settings,
  DollarSign, BarChart3, AlertTriangle, Loader2, Plus, Inbox, TrendingUp, FileText, Mail, Gift, Edit, Trash2, Star, Printer, Send
} from 'lucide-react';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api, { adminApi } from '../lib/api';
import AdminInbox, { ChannelBadge } from './AdminInbox';
import AdminAnalytics from '../components/AdminAnalytics';
import AdvancedPricing from '../components/AdvancedPricing';
import TieredPricing from '../components/TieredPricing';
import MonthlySummary from '../components/MonthlySummary';
import ReservationsMonthlyWidget from '../components/ReservationsMonthlyWidget';
import ReservationsFilter from '../components/ReservationsFilter';
import MarketingDashboard from '../components/MarketingDashboard';
import { DeletedReservations } from '../components/DeletedReservations';
import DiscountsManager from '../components/DiscountsManager';
import IncidentsManager from '../components/IncidentsManager';
import SurveysManager from '../components/SurveysManager';
import ExtrasManager from '../components/ExtrasManager';
import ManualInvoicing from '../components/ManualInvoicing';

const AdminDashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [activeSection, setActiveSection] = useState('reservations');
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [blackouts, setBlackouts] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [quickView, setQuickView] = useState('');
  const [advancedDate, setAdvancedDate] = useState(null); // Date filter for advanced search
  const [advancedFilterType, setAdvancedFilterType] = useState('all'); // 'arrivals', 'departures', 'cancelled', 'all'
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [showBlackoutDialog, setShowBlackoutDialog] = useState(false);
  const [showWorkerDialog, setShowWorkerDialog] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const [showDeleteWorkerDialog, setShowDeleteWorkerDialog] = useState(false);
  const [workerToDelete, setWorkerToDelete] = useState(null);
  const [pendingIncidents, setPendingIncidents] = useState(0);
  
  // Reservation edit/delete states
  const [showEditReservationDialog, setShowEditReservationDialog] = useState(false);
  const [showDeleteReservationDialog, setShowDeleteReservationDialog] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [reservationToDelete, setReservationToDelete] = useState(null);
  
  // Form states
  const [newPricing, setNewPricing] = useState({});
  const [newBlackout, setNewBlackout] = useState({ start: '', end: '', reason: '' });
  const [newWorker, setNewWorker] = useState({ name: '', email: '', password: '' });

  const locale = i18n.language === 'es' ? es : enUS;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, pricingRes, incidentsCountRes] = await Promise.all([
        adminApi.getStats(),
        adminApi.getPricing(),
        api.get('/admin/incidents/count').catch(() => ({ data: { count: 0 } }))
      ]);
      setStats(statsRes.data);
      setPricing(pricingRes.data);
      setNewPricing(pricingRes.data);
      setPendingIncidents(incidentsCountRes.data.count || 0);

      // Fetch reservations based on quick view or filters
      let reservationsRes;
      if (quickView === 'arrivals') {
        reservationsRes = await adminApi.getTodayArrivals();
        let filteredData = reservationsRes.data;
        // Apply search filter
        if (search) {
          const searchLower = search.toLowerCase();
          filteredData = filteredData.filter(r => 
            r.reservation_code?.toLowerCase().includes(searchLower) ||
            r.customer_name?.toLowerCase().includes(searchLower) ||
            r.customer_email?.toLowerCase().includes(searchLower) ||
            r.vehicle_plate?.toLowerCase().includes(searchLower) ||
            r.customer_phone?.includes(search)
          );
        }
        setReservations(filteredData);
        setTotal(filteredData.length);
      } else if (quickView === 'departures') {
        reservationsRes = await adminApi.getTodayDepartures();
        let filteredData = reservationsRes.data;
        // Apply search filter
        if (search) {
          const searchLower = search.toLowerCase();
          filteredData = filteredData.filter(r => 
            r.reservation_code?.toLowerCase().includes(searchLower) ||
            r.customer_name?.toLowerCase().includes(searchLower) ||
            r.customer_email?.toLowerCase().includes(searchLower) ||
            r.vehicle_plate?.toLowerCase().includes(searchLower) ||
            r.customer_phone?.includes(search)
          );
        }
        setReservations(filteredData);
        setTotal(filteredData.length);
      } else if (quickView === 'tomorrow') {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowStr = format(tomorrow, 'yyyy-MM-dd');
        reservationsRes = await adminApi.getReservationsByDate(tomorrowStr);
        const data = reservationsRes.data;
        let filteredData = data.reservations || data || [];
        // Apply search filter
        if (search) {
          const searchLower = search.toLowerCase();
          filteredData = filteredData.filter(r => 
            r.reservation_code?.toLowerCase().includes(searchLower) ||
            r.customer_name?.toLowerCase().includes(searchLower) ||
            r.customer_email?.toLowerCase().includes(searchLower) ||
            r.vehicle_plate?.toLowerCase().includes(searchLower) ||
            r.customer_phone?.includes(search)
          );
        }
        setReservations(filteredData);
        setTotal(filteredData.length);
      } else if (advancedDate) {
        // Filter by selected date with filter type
        const dateStr = format(advancedDate, 'yyyy-MM-dd');
        reservationsRes = await adminApi.getReservationsByDate(dateStr, advancedFilterType);
        const data = reservationsRes.data;
        let filteredReservations = data.reservations || data || [];
        
        // Additional client-side filter for cancelled
        if (advancedFilterType === 'cancelled') {
          filteredReservations = filteredReservations.filter(r => r.status === 'CANCELLED');
        }
        
        // Apply search filter
        if (search) {
          const searchLower = search.toLowerCase();
          filteredReservations = filteredReservations.filter(r => 
            r.reservation_code?.toLowerCase().includes(searchLower) ||
            r.customer_name?.toLowerCase().includes(searchLower) ||
            r.customer_email?.toLowerCase().includes(searchLower) ||
            r.vehicle_plate?.toLowerCase().includes(searchLower) ||
            r.customer_phone?.includes(search)
          );
        }
        
        setReservations(filteredReservations);
        setTotal(filteredReservations.length);
      } else {
        reservationsRes = await adminApi.getReservations({
          page,
          limit: 20,
          search: search || undefined,
          status: statusFilter || undefined
        });
        setReservations(reservationsRes.data.reservations);
        setTotal(reservationsRes.data.total);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, quickView, advancedDate, advancedFilterType]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [isAdmin, navigate, fetchData]);

  const fetchBlackouts = async () => {
    try {
      const res = await adminApi.getBlackouts();
      setBlackouts(res.data);
    } catch (error) {
      toast.error('Error al cargar bloqueos');
    }
  };

  const fetchWorkers = async () => {
    try {
      const res = await adminApi.getUsers('WORKER');
      setWorkers(res.data);
    } catch (error) {
      toast.error('Error al cargar trabajadores');
    }
  };

  const handleUpdatePricing = async () => {
    try {
      await adminApi.updatePricing(newPricing);
      toast.success('Tarifas actualizadas');
      setShowPricingDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar tarifas');
    }
  };

  const handleCreateBlackout = async () => {
    try {
      await adminApi.createBlackout(
        new Date(newBlackout.start).toISOString(),
        new Date(newBlackout.end).toISOString(),
        newBlackout.reason
      );
      toast.success('Bloqueo creado');
      setShowBlackoutDialog(false);
      setNewBlackout({ start: '', end: '', reason: '' });
      fetchBlackouts();
    } catch (error) {
      toast.error('Error al crear bloqueo');
    }
  };

  const handleDeleteBlackout = async (id) => {
    try {
      await adminApi.deleteBlackout(id);
      toast.success('Bloqueo eliminado');
      fetchBlackouts();
    } catch (error) {
      toast.error('Error al eliminar bloqueo');
    }
  };

  const handleCreateWorker = async () => {
    try {
      if (editingWorker) {
        // Update existing worker
        await adminApi.updateUser(editingWorker.id, {
          name: newWorker.name,
          email: newWorker.email,
          ...(newWorker.password && { password: newWorker.password })
        });
        toast.success('Trabajador actualizado');
      } else {
        // Create new worker
        await adminApi.createUser({
          ...newWorker,
          role: 'WORKER',
          language: 'es'
        });
        toast.success('Trabajador creado');
      }
      setShowWorkerDialog(false);
      setNewWorker({ name: '', email: '', password: '' });
      setEditingWorker(null);
      fetchWorkers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar trabajador');
    }
  };

  const handleEditWorker = (worker) => {
    setEditingWorker(worker);
    setNewWorker({ name: worker.name, email: worker.email, password: '' });
    setShowWorkerDialog(true);
  };

  const handleDeleteWorker = async () => {
    if (!workerToDelete) return;
    try {
      await adminApi.deleteUser(workerToDelete.id);
      toast.success('Trabajador eliminado');
      setShowDeleteWorkerDialog(false);
      setWorkerToDelete(null);
      fetchWorkers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar trabajador');
    }
  };

  // Reservation Edit/Delete handlers
  const handleEditReservation = (reservation) => {
    setEditingReservation({ ...reservation });
    setShowEditReservationDialog(true);
  };

  const handleSaveReservation = async () => {
    if (!editingReservation) return;
    try {
      await adminApi.updateReservation(editingReservation.id, editingReservation);
      toast.success('Reserva actualizada correctamente');
      setShowEditReservationDialog(false);
      setEditingReservation(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar la reserva');
    }
  };

  const handleDeleteReservation = async () => {
    if (!reservationToDelete) return;
    try {
      await adminApi.deleteReservation(reservationToDelete.id);
      toast.success(`Reserva ${reservationToDelete.reservation_code} eliminada`);
      setShowDeleteReservationDialog(false);
      setReservationToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar la reserva');
    }
  };

  const handleResendEmail = async (reservation) => {
    try {
      await adminApi.resendReservationEmail(reservation.id);
      toast.success(`Email reenviado a ${reservation.customer_email}`);
    } catch (error) {
      console.error('Error resending email:', error);
      toast.error(error.response?.data?.detail || 'Error al reenviar el email');
    }
  };

  const exportCSV = () => {
    const headers = ['Código', 'Matrícula', 'Cliente', 'Email', 'Entrada', 'Salida', 'Estado', 'Pago', 'Factura', 'Total'];
    const rows = reservations.map(r => [
      r.reservation_code,
      r.vehicle_plate,
      r.customer_name,
      r.customer_email,
      r.start_dt,
      r.end_dt,
      r.status,
      r.payment_status,
      (r.total_cents / 100).toFixed(2) + '€'
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reservas_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const handlePrintReservations = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Reservas Privilege Parking - ${format(new Date(), 'dd/MM/yyyy')}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1a1a1a; font-size: 24px; margin-bottom: 5px; }
          h2 { color: #666; font-size: 14px; font-weight: normal; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th { background: #1a1a1a; color: #FFD700; padding: 10px 8px; text-align: left; }
          td { padding: 8px; border-bottom: 1px solid #ddd; }
          tr:nth-child(even) { background: #f9f9f9; }
          .total-row { background: #1a1a1a !important; color: #FFD700; font-weight: bold; }
          .status-confirmed { color: #22c55e; font-weight: bold; }
          .status-pending { color: #f59e0b; }
          .status-cancelled { color: #ef4444; }
          .summary { margin-top: 20px; padding: 15px; background: #f5f5f5; border-radius: 8px; }
          .summary-item { display: inline-block; margin-right: 30px; }
          .summary-label { color: #666; font-size: 12px; }
          .summary-value { font-size: 18px; font-weight: bold; color: #1a1a1a; }
          @media print {
            body { padding: 0; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h1>🚗 PRIVILEGE PARKING</h1>
        <h2>Listado de Reservas - ${format(new Date(), "EEEE dd 'de' MMMM yyyy", { locale })}</h2>
        
        <div class="summary">
          <div class="summary-item">
            <div class="summary-label">Total Reservas</div>
            <div class="summary-value">${reservations.length}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Importe Total</div>
            <div class="summary-value">${(reservations.reduce((sum, r) => sum + (r.total_cents || 0), 0) / 100).toFixed(2)}€</div>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Matrícula</th>
              <th>Cliente</th>
              <th>Entrada</th>
              <th>Salida</th>
              <th>Terminal</th>
              <th>Estado</th>
              <th>Pago</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            ${reservations.map(r => `
              <tr>
                <td><strong>${esc(r.vehicle_plate || '-')}</strong></td>
                <td>${esc(r.customer_name || '-')}</td>
                <td>${r.start_dt ? esc(format(new Date(r.start_dt), 'dd/MM HH:mm')) : '-'}</td>
                <td>${r.end_dt ? esc(format(new Date(r.end_dt), 'dd/MM HH:mm')) : '-'}</td>
                <td>${esc(r.arrival_terminal || 'T4')}</td>
                <td class="${r.status === 'CONFIRMED' ? 'status-confirmed' : r.status === 'CANCELLED' ? 'status-cancelled' : 'status-pending'}">${esc(r.status || '-')}</td>
                <td>${r.payment_status === 'PAID' ? '✅ PAGADO' : '⏳ COBRAR'}</td>
                <td><strong>${((r.total_cents || 0) / 100).toFixed(2)}€</strong></td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="7" style="text-align: right;">TOTAL:</td>
              <td><strong>${(reservations.reduce((sum, r) => sum + (r.total_cents || 0), 0) / 100).toFixed(2)}€</strong></td>
            </tr>
          </tbody>
        </table>
        
        <p style="margin-top: 30px; font-size: 10px; color: #999; text-align: center;">
          Impreso el ${format(new Date(), "dd/MM/yyyy 'a las' HH:mm")} | Privilege Parking - Aeropuerto de Barajas
        </p>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const formatDate = (dateStr) => format(new Date(dateStr), 'dd/MM/yy HH:mm', { locale });

  const getStatusColor = (status) => {
    const colors = {
      PENDING_PAYMENT: 'bg-warning/20 text-warning',
      CONFIRMED: 'bg-success/20 text-success',
      CANCELLED: 'bg-destructive/20 text-destructive',
      COMPLETED: 'bg-primary/20 text-primary',
      NO_SHOW: 'bg-zinc-500/20 text-zinc-400'
    };
    return colors[status] || 'bg-zinc-500/20 text-zinc-400';
  };

  if (loading && !reservations.length) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-card border-r border-white/5 z-50 hidden lg:block overflow-y-auto">
        <div className="p-4 border-b border-white/5">
          <Link to="/" className="flex items-center gap-2">
            <Logo className="h-10" />
            <span className="font-heading font-bold text-white">ADMIN</span>
          </Link>
        </div>

        <nav className="p-4 space-y-2 relative z-10">
          <button
            onClick={() => { setActiveSection('reservations'); setQuickView(''); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors relative z-20 ${
              activeSection === 'reservations' ? 'bg-primary text-black' : 'text-zinc-400 hover:bg-secondary'
            }`}
            data-testid="nav-reservations"
          >
            <CalendarIcon className="w-5 h-5" />
            {t('admin.reservations')}
          </button>

          <button
            onClick={() => { setActiveSection('deleted'); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeSection === 'deleted' ? 'bg-primary text-black' : 'text-zinc-400 hover:bg-secondary'
            }`}
            data-testid="nav-deleted"
          >
            <Trash2 className="w-5 h-5" />
            Eliminadas
          </button>
          
          <button
            onClick={() => { setActiveSection('pricing'); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeSection === 'pricing' ? 'bg-primary text-black' : 'text-zinc-400 hover:bg-secondary'
            }`}
            data-testid="nav-pricing"
          >
            <DollarSign className="w-5 h-5" />
            {t('admin.pricing')}
          </button>
          
          <button
            onClick={() => { setActiveSection('blackouts'); fetchBlackouts(); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeSection === 'blackouts' ? 'bg-primary text-black' : 'text-zinc-400 hover:bg-secondary'
            }`}
            data-testid="nav-blackouts"
          >
            <AlertTriangle className="w-5 h-5" />
            {t('admin.blackouts')}
          </button>
          
          <button
            onClick={() => { setActiveSection('workers'); fetchWorkers(); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeSection === 'workers' ? 'bg-primary text-black' : 'text-zinc-400 hover:bg-secondary'
            }`}
            data-testid="nav-workers"
          >
            <Users className="w-5 h-5" />
            {t('admin.workers')}
          </button>

          <button
            onClick={() => { setActiveSection('inbox'); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeSection === 'inbox' ? 'bg-primary text-black' : 'text-zinc-400 hover:bg-secondary'
            }`}
            data-testid="nav-inbox"
          >
            <Inbox className="w-5 h-5" />
            Canales
          </button>

          <button
            onClick={() => { setActiveSection('analytics'); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeSection === 'analytics' ? 'bg-primary text-black' : 'text-zinc-400 hover:bg-secondary'
            }`}
            data-testid="nav-analytics"
          >
            <TrendingUp className="w-5 h-5" />
            Analytics
          </button>

          <button
            onClick={() => { setActiveSection('marketing'); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeSection === 'marketing' ? 'bg-primary text-black' : 'text-zinc-400 hover:bg-secondary'
            }`}
            data-testid="nav-marketing"
          >
            <Mail className="w-5 h-5" />
            Marketing
          </button>

          <button
            onClick={() => { setActiveSection('discounts'); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeSection === 'discounts' ? 'bg-primary text-black' : 'text-zinc-400 hover:bg-secondary'
            }`}
            data-testid="nav-discounts"
          >
            <Gift className="w-5 h-5" />
            Descuentos
          </button>

          <button
            onClick={() => { setActiveSection('extras'); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeSection === 'extras' ? 'bg-primary text-black' : 'text-zinc-400 hover:bg-secondary'
            }`}
            data-testid="nav-extras"
          >
            <Plus className="w-5 h-5" />
            Extras
          </button>

          <button
            onClick={() => { setActiveSection('surveys'); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeSection === 'surveys' ? 'bg-primary text-black' : 'text-zinc-400 hover:bg-secondary'
            }`}
            data-testid="nav-surveys"
          >
            <Star className="w-5 h-5" />
            Encuestas
          </button>

          <button
            onClick={() => { setActiveSection('incidents'); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors relative ${
              activeSection === 'incidents' ? 'bg-primary text-black' : 'text-zinc-400 hover:bg-secondary'
            }`}
            data-testid="nav-incidents"
          >
            <AlertTriangle className="w-5 h-5" />
            Incidencias
            {pendingIncidents > 0 && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {pendingIncidents > 9 ? '9+' : pendingIncidents}
              </span>
            )}
          </button>

          <button
            onClick={() => { setActiveSection('summary'); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeSection === 'summary' ? 'bg-primary text-black' : 'text-zinc-400 hover:bg-secondary'
            }`}
            data-testid="nav-summary"
          >
            <FileText className="w-5 h-5" />
            Resumen Mensual
          </button>

          <button
            onClick={() => { setActiveSection('invoicing'); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeSection === 'invoicing' ? 'bg-primary text-black' : 'text-zinc-400 hover:bg-secondary'
            }`}
            data-testid="nav-invoicing"
          >
            <DollarSign className="w-5 h-5" />
            Facturación
          </button>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/5">
          <div className="flex items-center justify-between">
            <span className="text-sm text-zinc-400">{user?.name}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { logout(); navigate('/'); }}
              className="text-zinc-400 hover:text-red-400 hover:bg-red-400/10 flex items-center gap-2"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Salir</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        {/* Header */}
        <header className="glass-heavy sticky top-0 z-30 px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="font-heading text-xl font-bold text-white">
              {activeSection === 'reservations' && t('admin.reservations')}
              {activeSection === 'pricing' && t('admin.pricing')}
              {activeSection === 'blackouts' && t('admin.blackouts')}
              {activeSection === 'workers' && t('admin.workers')}
              {activeSection === 'inbox' && 'Canales'}
              {activeSection === 'analytics' && 'Analytics'}
              {activeSection === 'marketing' && 'Marketing'}
              {activeSection === 'discounts' && 'Descuentos'}
              {activeSection === 'extras' && 'Extras'}
              {activeSection === 'surveys' && 'Encuestas'}
              {activeSection === 'incidents' && 'Incidencias'}
              {activeSection === 'summary' && 'Resumen Mensual'}
              {activeSection === 'invoicing' && 'Facturación'}
              {activeSection === 'deleted' && 'Eliminadas'}
            </h1>
            
            {/* Mobile nav */}
            <div className="lg:hidden flex items-center gap-2">
              <Select value={activeSection} onValueChange={setActiveSection}>
                <SelectTrigger className="w-40 input-gold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  <SelectItem value="reservations">Reservas</SelectItem>
                  <SelectItem value="pricing">Tarifas</SelectItem>
                  <SelectItem value="blackouts">Bloqueos</SelectItem>
                  <SelectItem value="workers">Trabajadores</SelectItem>
                  <SelectItem value="inbox">Canales</SelectItem>
                  <SelectItem value="analytics">Analytics</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="discounts">Descuentos</SelectItem>
                  <SelectItem value="extras">Extras</SelectItem>
                  <SelectItem value="summary">Resumen Mensual</SelectItem>
                  <SelectItem value="invoicing">Facturación</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </header>

        <div className="p-6">
          {/* Stats */}
          {stats && activeSection === 'reservations' && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="card-dark p-4">
                <p className="text-zinc-400 text-sm">Total Reservas</p>
                <p className="text-2xl font-bold text-white">{stats.total_reservations}</p>
              </div>
              <div className="card-dark p-4">
                <p className="text-zinc-400 text-sm">Hoy</p>
                <p className="text-2xl font-bold text-primary">{stats.today_reservations}</p>
              </div>
              <div className="card-dark p-4">
                <p className="text-zinc-400 text-sm">Confirmadas</p>
                <p className="text-2xl font-bold text-success">{stats.confirmed}</p>
              </div>
              <div className="card-dark p-4">
                <p className="text-zinc-400 text-sm">Ingresos Totales</p>
                <p className="text-2xl font-bold text-primary">{stats.total_revenue_euros?.toFixed(2) || '0.00'}€</p>
                <div className="flex gap-3 mt-1 text-xs">
                  <span className="text-green-400">
                    Cobrado: {stats.paid_revenue_euros?.toFixed(2) || '0.00'}€
                  </span>
                  <span className="text-amber-400">
                    Pendiente: {stats.pending_revenue_euros?.toFixed(2) || '0.00'}€
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Reservations Section */}
          {activeSection === 'reservations' && (
            <>
              {/* Monthly Summary Widget */}
              <ReservationsMonthlyWidget />

              {/* New Date-Based Filter */}
              <ReservationsFilter />
              
              {/* Divider */}
              <div className="my-8 border-t border-white/10" />
              
              {/* Legacy Reservations Table */}
              <h3 className="text-lg font-semibold text-white mb-4">
                {i18n.language === 'es' ? 'Búsqueda Avanzada' : 'Advanced Search'}
              </h3>
              
              {/* Quick Views */}
              <div className="flex flex-wrap gap-2 mb-4 items-center">
                {/* Date Picker with Calendar Popover */}
                <div className="flex items-center gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-44 justify-start text-left font-normal bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {advancedDate ? format(advancedDate, 'dd/MM/yyyy', { locale: es }) : 'Seleccionar fecha'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#1a1a1a] border-white/20 z-[100]" align="start">
                      <Calendar
                        mode="single"
                        selected={advancedDate}
                        onSelect={(date) => {
                          if (date) {
                            setAdvancedDate(date);
                            setQuickView('');
                            setPage(1);
                          }
                        }}
                        locale={es}
                        className="rounded-md"
                      />
                    </PopoverContent>
                  </Popover>
                  {advancedDate && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setAdvancedDate(null); setAdvancedFilterType('all'); }}
                      className="text-zinc-400 hover:text-white px-2"
                    >
                      ✕
                    </Button>
                  )}
                </div>
                
                {/* Filter Type Buttons - Only show when date is selected */}
                {advancedDate && (
                  <>
                    <div className="h-6 w-px bg-zinc-600 mx-2" />
                    <Button
                      size="sm"
                      onClick={() => { setAdvancedFilterType('arrivals'); setPage(1); }}
                      className={advancedFilterType === 'arrivals' ? 'bg-green-600 hover:bg-green-700 text-white' : 'btn-secondary'}
                    >
                      Entradas
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => { setAdvancedFilterType('departures'); setPage(1); }}
                      className={advancedFilterType === 'departures' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'btn-secondary'}
                    >
                      Salidas
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => { setAdvancedFilterType('cancelled'); setPage(1); }}
                      className={advancedFilterType === 'cancelled' ? 'bg-red-600 hover:bg-red-700 text-white' : 'btn-secondary'}
                    >
                      Canceladas
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => { setAdvancedFilterType('all'); setPage(1); }}
                      className={advancedFilterType === 'all' ? 'btn-primary' : 'btn-secondary'}
                    >
                      Todas
                    </Button>
                  </>
                )}
                
                {/* Quick filters when no date selected */}
                {!advancedDate && (
                  <>
                    <Button
                      size="sm"
                      onClick={() => { setQuickView('arrivals'); setPage(1); }}
                      className={quickView === 'arrivals' ? 'bg-green-600 hover:bg-green-700 text-white' : 'btn-secondary'}
                    >
                      {t('admin.todayArrivals')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => { setQuickView('departures'); setPage(1); }}
                      className={quickView === 'departures' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'btn-secondary'}
                    >
                      {t('admin.todayDepartures')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => { setQuickView(''); setPage(1); }}
                      className={!quickView ? 'btn-primary' : 'btn-secondary'}
                    >
                      Todas
                    </Button>
                  </>
                )}
                
                {/* Print Button */}
                <Button
                  size="sm"
                  onClick={() => handlePrintReservations()}
                  className="btn-secondary ml-auto"
                  data-testid="print-reservations"
                >
                  <Printer className="w-4 h-4 mr-1" />
                  Imprimir
                </Button>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={t('admin.search')}
                      className="input-gold pl-10"
                      data-testid="search-input"
                    />
                  </div>
                </div>
                
                <Select value={statusFilter || "ALL"} onValueChange={(v) => setStatusFilter(v === "ALL" ? "" : v)}>
                  <SelectTrigger className="w-40 input-gold">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    <SelectItem value="ALL">Todos</SelectItem>
                    <SelectItem value="CONFIRMED">Confirmadas</SelectItem>
                    <SelectItem value="PENDING_PAYMENT">Pendientes</SelectItem>
                    <SelectItem value="COMPLETED">Completadas</SelectItem>
                    <SelectItem value="CANCELLED">Canceladas</SelectItem>
                  </SelectContent>
                </Select>

                <Button onClick={exportCSV} className="btn-secondary" data-testid="export-btn">
                  <Download className="w-4 h-4 mr-2" />
                  {t('admin.export')}
                </Button>
              </div>

              {/* Table */}
              <div className="card-dark overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-secondary">
                      <tr>
                        <th className="text-left p-4 text-zinc-400 text-sm font-medium">Código</th>
                        <th className="text-left p-4 text-zinc-400 text-sm font-medium">Canal</th>
                        <th className="text-left p-4 text-zinc-400 text-sm font-medium">Matrícula</th>
                        <th className="text-left p-4 text-zinc-400 text-sm font-medium">Cliente</th>
                        <th className="text-left p-4 text-zinc-400 text-sm font-medium">Entrada</th>
                        <th className="text-left p-4 text-zinc-400 text-sm font-medium">Salida</th>
                        <th className="text-left p-4 text-zinc-400 text-sm font-medium">Estado</th>
                        <th className="text-left p-4 text-zinc-400 text-sm font-medium">Pago</th>
                        <th className="text-center p-4 text-zinc-400 text-sm font-medium">Factura</th>
                        <th className="text-right p-4 text-zinc-400 text-sm font-medium">Total</th>
                        <th className="text-center p-4 text-zinc-400 text-sm font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reservations.map((r) => (
                        <tr key={r.id} className="border-t border-white/5 hover:bg-secondary/50" data-testid={`row-${r.id}`}>
                          <td className="p-4">
                            <span className="font-mono text-primary">{r.reservation_code}</span>
                            {r.external_booking_id && (
                              <p className="text-xs text-zinc-500 mt-1">{r.external_booking_id}</p>
                            )}
                          </td>
                          <td className="p-4">
                            <ChannelBadge channel={r.source_channel || 'DIRECT'} />
                          </td>
                          <td className="p-4">
                            <span className="font-mono text-white">{r.vehicle_plate}</span>
                          </td>
                          <td className="p-4">
                            <p className="text-white text-sm">{r.customer_name}</p>
                            <p className="text-zinc-500 text-xs">{r.customer_email}</p>
                          </td>
                          <td className="p-4 text-sm text-zinc-300">{formatDate(r.start_dt)}</td>
                          <td className="p-4 text-sm text-zinc-300">{formatDate(r.end_dt)}</td>
                          <td className="p-4">
                            <span className={`text-xs px-2 py-1 rounded ${getStatusColor(r.status)}`}>
                              {t(`status.${r.status}`)}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className={`text-xs px-2 py-1 rounded font-semibold ${
                              r.payment_status === 'PAID' 
                                ? 'bg-success/20 text-success' 
                                : 'bg-warning/20 text-warning'
                            }`}>
                              {r.payment_status === 'PAID' 
                                ? (r.paid_via_channel ? 'PREPAGADO' : 'PAGADO')
                                : 'COBRO EN PARKING'}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <span className={`text-xs px-2 py-1 rounded font-semibold ${
                              r.wants_invoice 
                                ? 'bg-blue-500/20 text-blue-400' 
                                : 'bg-zinc-700/50 text-zinc-500'
                            }`}>
                              {r.wants_invoice ? 'SÍ' : 'NO'}
                            </span>
                          </td>
                          <td className="p-4 text-right">
                            <span className="font-mono text-white">{(r.total_cents / 100).toFixed(2)}€</span>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 px-2 text-primary border-primary/50 hover:bg-primary/10"
                                onClick={() => handleEditReservation(r)}
                                data-testid={`edit-reservation-${r.id}`}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Editar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 px-2 text-blue-500 border-blue-500/50 hover:bg-blue-500/10"
                                onClick={() => window.open(`/reserva/${r.reservation_code}/print`, '_blank')}
                                data-testid={`print-reservation-${r.id}`}
                              >
                                <Printer className="w-4 h-4 mr-1" />
                                Imprimir
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 px-2 text-green-400 border-green-400/50 hover:bg-green-500/10"
                                onClick={() => handleResendEmail(r)}
                                data-testid={`resend-email-${r.id}`}
                              >
                                <Send className="w-4 h-4 mr-1" />
                                Reenviar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 px-2 text-red-500 border-red-500/50 hover:bg-red-500/10"
                                onClick={() => {
                                  setReservationToDelete(r);
                                  setShowDeleteReservationDialog(true);
                                }}
                                data-testid={`delete-reservation-${r.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Eliminar
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {!quickView && (
                  <div className="flex items-center justify-between p-4 border-t border-white/5">
                    <div className="flex items-center gap-6">
                      <p className="text-sm text-zinc-400">
                        Mostrando {reservations.length} de {total}
                      </p>
                      <div className="flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-lg">
                        <span className="text-sm text-zinc-400">Suma total:</span>
                        <span className="text-lg font-bold text-primary">
                          {reservations.reduce((sum, r) => sum + (r.total_cents || 0), 0) / 100}€
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        data-testid="prev-page"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-zinc-400 px-2">{page}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPage(p => p + 1)}
                        disabled={reservations.length < 20}
                        data-testid="next-page"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Pricing Section - With Mode Tabs */}
          {activeSection === 'deleted' && (<DeletedReservations />)}

          {activeSection === 'pricing' && (
            <div className="space-y-6">
              {/* Operating Hours Configuration */}
              <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4" data-testid="operating-hours-block">
                <h3 className="text-white font-medium mb-1">Horario operativo</h3>
                <p className="text-xs text-zinc-500 mb-4">
                  Define las horas en las que los clientes pueden seleccionar entrada y salida.
                  Las horas fuera del rango quedarán ocultas en el wizard de reservas. Granularidad de 15 minutos.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1" htmlFor="op-start-time">
                      Hora de apertura
                    </label>
                    <input
                      id="op-start-time"
                      type="time"
                      step="900"
                      data-testid="operating-start-time"
                      value={`${String(pricing?.operating_start_hour ?? 4).padStart(2, '0')}:${String(pricing?.operating_start_minute ?? 0).padStart(2, '0')}`}
                      onChange={(e) => {
                        const [h, m] = (e.target.value || '04:00').split(':').map(n => parseInt(n, 10) || 0);
                        setPricing(prev => ({ ...prev, operating_start_hour: h, operating_start_minute: m }));
                      }}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white text-sm focus:border-primary focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1" htmlFor="op-end-time">
                      Hora de cierre
                    </label>
                    <input
                      id="op-end-time"
                      type="time"
                      step="900"
                      data-testid="operating-end-time"
                      value={(() => {
                        const h = pricing?.operating_end_hour ?? 24;
                        const m = pricing?.operating_end_minute ?? 0;
                        // 24:00 shown as 00:00 (midnight) in the picker
                        if (h >= 24) return '00:00';
                        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                      })()}
                      onChange={(e) => {
                        const raw = e.target.value || '00:00';
                        let [h, m] = raw.split(':').map(n => parseInt(n, 10) || 0);
                        // Treat 00:00 as midnight = 24:00 (end of operating day)
                        if (h === 0 && m === 0) { h = 24; m = 0; }
                        setPricing(prev => ({ ...prev, operating_end_hour: h, operating_end_minute: m }));
                      }}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white text-sm focus:border-primary focus:outline-none"
                    />
                    <p className="text-[11px] text-zinc-500 mt-1">Selecciona 00:00 para cerrar a medianoche.</p>
                  </div>
                  <Button
                    data-testid="save-operating-hours-btn"
                    className="btn-primary"
                    onClick={async () => {
                      const sh = parseInt(pricing?.operating_start_hour ?? 4, 10);
                      const sm = parseInt(pricing?.operating_start_minute ?? 0, 10);
                      const eh = parseInt(pricing?.operating_end_hour ?? 24, 10);
                      const em = parseInt(pricing?.operating_end_minute ?? 0, 10);
                      const startTotal = sh * 60 + sm;
                      const endTotal = eh * 60 + em;
                      if (endTotal <= startTotal) {
                        toast.error('La hora de cierre debe ser posterior a la de apertura');
                        return;
                      }
                      try {
                        await adminApi.updatePricing({
                          ...pricing,
                          operating_start_hour: sh,
                          operating_start_minute: sm,
                          operating_end_hour: eh,
                          operating_end_minute: em,
                        });
                        toast.success('Horario operativo actualizado');
                      } catch (e) {
                        toast.error('Error al guardar el horario');
                      }
                    }}
                  >
                    Guardar horario
                  </Button>
                </div>
              </div>

              {/* Mode Selector Tabs */}
              <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
                <h3 className="text-white font-medium mb-4">Modo de Precios</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={async () => {
                      try {
                        await adminApi.updatePricing({ ...pricing, pricing_mode: 'SIMPLE' });
                        setPricing(prev => ({ ...prev, pricing_mode: 'SIMPLE' }));
                        toast.success('Modo SIMPLE activado');
                      } catch (e) { toast.error('Error al cambiar modo'); }
                    }}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      pricing?.pricing_mode === 'SIMPLE' 
                        ? 'bg-primary/10 border-primary text-white' 
                        : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-500'
                    }`}
                  >
                    <p className="font-medium text-lg mb-1">Simple</p>
                    <p className="text-sm opacity-70">Precio fijo por día</p>
                    <p className="text-xs mt-2 opacity-50">Ej: 12€/día × 5 días = 60€</p>
                  </button>
                  
                  <button
                    onClick={async () => {
                      try {
                        await adminApi.updatePricing({ ...pricing, pricing_mode: 'TIERED' });
                        setPricing(prev => ({ ...prev, pricing_mode: 'TIERED' }));
                        toast.success('Modo AVANZADO activado');
                      } catch (e) { toast.error('Error al cambiar modo'); }
                    }}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      pricing?.pricing_mode === 'TIERED' || pricing?.pricing_mode === 'ADVANCED'
                        ? 'bg-primary/10 border-primary text-white' 
                        : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-500'
                    }`}
                  >
                    <p className="font-medium text-lg mb-1">Avanzado</p>
                    <p className="text-sm opacity-70">Precio diferente para cada día (1-30)</p>
                    <p className="text-xs mt-2 opacity-50">Ej: Día 1=15€, Día 2=14€, Día 30=8€...</p>
                  </button>
                </div>
              </div>
              
              {/* Mode-specific configuration */}
              {pricing?.pricing_mode === 'TIERED' || pricing?.pricing_mode === 'ADVANCED' ? (
                <TieredPricing />
              ) : (
                <AdvancedPricing />
              )}
            </div>
          )}

          {/* Blackouts Section */}
          {activeSection === 'blackouts' && (
            <div className="max-w-4xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-heading text-xl font-bold text-white">Periodos de Bloqueo</h2>
                <Button onClick={() => setShowBlackoutDialog(true)} className="btn-primary" data-testid="add-blackout-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Añadir Bloqueo
                </Button>
              </div>

              <div className="space-y-4">
                {blackouts.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No hay bloqueos configurados</p>
                ) : (
                  blackouts.map((b) => (
                    <div key={b.id} className="card-dark p-4 flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{b.reason}</p>
                        <p className="text-zinc-400 text-sm">
                          {formatDate(b.start_dt)} - {formatDate(b.end_dt)}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteBlackout(b.id)}
                        data-testid={`delete-blackout-${b.id}`}
                      >
                        Eliminar
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Workers Section */}
          {activeSection === 'workers' && (
            <div className="max-w-4xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-heading text-xl font-bold text-white">Trabajadores</h2>
                <Button onClick={() => { setEditingWorker(null); setNewWorker({ name: '', email: '', password: '' }); setShowWorkerDialog(true); }} className="btn-primary" data-testid="add-worker-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Añadir Trabajador
                </Button>
              </div>

              <div className="space-y-4">
                {workers.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No hay trabajadores</p>
                ) : (
                  workers.map((w) => (
                    <div key={w.id} className="card-dark p-4 flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{w.name}</p>
                        <p className="text-zinc-400 text-sm">{w.email}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs px-2 py-1 rounded bg-primary/20 text-primary">
                          {w.role}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditWorker(w)}
                          className="text-zinc-400 hover:text-white"
                          data-testid={`edit-worker-${w.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setWorkerToDelete(w); setShowDeleteWorkerDialog(true); }}
                          className="text-red-400 hover:text-red-300"
                          data-testid={`delete-worker-${w.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Inbox (Channel Integration) */}
          {activeSection === 'inbox' && (
            <AdminInbox />
          )}

          {/* Analytics Section */}
          {activeSection === 'analytics' && (
            <AdminAnalytics />
          )}

          {/* Marketing Section */}
          {activeSection === 'marketing' && (
            <MarketingDashboard />
          )}

          {/* Discounts Section */}
          {activeSection === 'discounts' && (
            <DiscountsManager />
          )}

          {/* Extras Section */}
          {activeSection === 'extras' && (
            <ExtrasManager />
          )}

          {/* Surveys Section */}
          {activeSection === 'surveys' && (
            <SurveysManager />
          )}

          {/* Incidents Section */}
          {activeSection === 'incidents' && (
            <IncidentsManager />
          )}

          {/* Monthly Summary Section */}
          {activeSection === 'summary' && (
            <MonthlySummary />
          )}

          {/* Manual Invoicing Section */}
          {activeSection === 'invoicing' && (
            <ManualInvoicing />
          )}
        </div>
      </main>

      {/* Pricing Dialog */}
      <Dialog open={showPricingDialog} onOpenChange={setShowPricingDialog}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Editar Tarifas</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white">Tarifa por día (céntimos)</Label>
              <Input
                type="number"
                value={newPricing.daily_rate_cents || ''}
                onChange={(e) => setNewPricing({ ...newPricing, daily_rate_cents: parseInt(e.target.value) })}
                className="input-gold"
                data-testid="pricing-daily-input"
              />
              <p className="text-xs text-zinc-500 mt-1">Ejemplo: 1200 = 12€/día</p>
            </div>
            <div>
              <Label className="text-white">Mínimo días</Label>
              <Input
                type="number"
                value={newPricing.min_days || ''}
                onChange={(e) => setNewPricing({ ...newPricing, min_days: parseInt(e.target.value) })}
                className="input-gold"
                data-testid="pricing-min-days-input"
              />
            </div>
            <div>
              <Label className="text-white">Antelación mínima (horas)</Label>
              <Input
                type="number"
                value={newPricing.min_advance_hours || ''}
                onChange={(e) => setNewPricing({ ...newPricing, min_advance_hours: parseInt(e.target.value) })}
                className="input-gold"
                data-testid="pricing-advance-input"
              />
              <p className="text-xs text-zinc-500 mt-1">Horas mínimas antes de la entrada para poder reservar</p>
            </div>
            <div>
              <Label className="text-white">Máximo días por reserva</Label>
              <Input
                type="number"
                value={newPricing.max_days || ''}
                onChange={(e) => setNewPricing({ ...newPricing, max_days: parseInt(e.target.value) })}
                className="input-gold"
                data-testid="pricing-max-days-input"
              />
            </div>
            <Button onClick={handleUpdatePricing} className="w-full btn-primary" data-testid="save-pricing-btn">
              Guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Blackout Dialog */}
      <Dialog open={showBlackoutDialog} onOpenChange={setShowBlackoutDialog}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Nuevo Bloqueo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white">Fecha/hora inicio</Label>
              <Input
                type="datetime-local"
                value={newBlackout.start}
                onChange={(e) => setNewBlackout({ ...newBlackout, start: e.target.value })}
                className="input-gold"
                data-testid="blackout-start-input"
              />
            </div>
            <div>
              <Label className="text-white">Fecha/hora fin</Label>
              <Input
                type="datetime-local"
                value={newBlackout.end}
                onChange={(e) => setNewBlackout({ ...newBlackout, end: e.target.value })}
                className="input-gold"
                data-testid="blackout-end-input"
              />
            </div>
            <div>
              <Label className="text-white">Motivo</Label>
              <Input
                value={newBlackout.reason}
                onChange={(e) => setNewBlackout({ ...newBlackout, reason: e.target.value })}
                placeholder="Mantenimiento, cierre, etc."
                className="input-gold"
                data-testid="blackout-reason-input"
              />
            </div>
            <Button 
              onClick={handleCreateBlackout} 
              className="w-full btn-primary"
              disabled={!newBlackout.start || !newBlackout.end || !newBlackout.reason}
              data-testid="save-blackout-btn"
            >
              Crear Bloqueo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Worker Dialog */}
      <Dialog open={showWorkerDialog} onOpenChange={(open) => { setShowWorkerDialog(open); if (!open) setEditingWorker(null); }}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">{editingWorker ? 'Editar Trabajador' : 'Nuevo Trabajador'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white">Nombre</Label>
              <Input
                value={newWorker.name}
                onChange={(e) => setNewWorker({ ...newWorker, name: e.target.value })}
                placeholder="Nombre completo"
                className="input-gold"
                data-testid="worker-name-input"
              />
            </div>
            <div>
              <Label className="text-white">Email</Label>
              <Input
                type="email"
                value={newWorker.email}
                onChange={(e) => setNewWorker({ ...newWorker, email: e.target.value })}
                placeholder="email@ejemplo.com"
                className="input-gold"
                data-testid="worker-email-input"
              />
            </div>
            <div>
              <Label className="text-white">Contraseña {editingWorker && <span className="text-zinc-500 text-xs">(dejar vacío para mantener)</span>}</Label>
              <Input
                type="password"
                value={newWorker.password}
                onChange={(e) => setNewWorker({ ...newWorker, password: e.target.value })}
                placeholder={editingWorker ? "Nueva contraseña (opcional)" : "••••••••"}
                className="input-gold"
                data-testid="worker-password-input"
              />
            </div>
            <Button 
              onClick={handleCreateWorker} 
              className="w-full btn-primary"
              disabled={!newWorker.name || !newWorker.email || (!editingWorker && !newWorker.password)}
              data-testid="save-worker-btn"
            >
              {editingWorker ? 'Guardar Cambios' : 'Crear Trabajador'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Worker Confirmation Dialog */}
      <Dialog open={showDeleteWorkerDialog} onOpenChange={setShowDeleteWorkerDialog}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Eliminar Trabajador</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-zinc-400">
              ¿Estás seguro de que quieres eliminar a <strong className="text-white">{workerToDelete?.name}</strong>?
            </p>
            <p className="text-sm text-red-400">Esta acción no se puede deshacer.</p>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowDeleteWorkerDialog(false)} 
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleDeleteWorker} 
                className="flex-1 bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-worker-btn"
              >
                Eliminar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Reservation Dialog */}
      <Dialog open={showEditReservationDialog} onOpenChange={(open) => { setShowEditReservationDialog(open); if (!open) setEditingReservation(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary" />
              Editar Reserva {editingReservation?.reservation_code}
            </DialogTitle>
          </DialogHeader>
          {editingReservation && (
            <div className="space-y-4 py-4">
              {/* Customer Info */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-zinc-400">Datos del Cliente</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-400 text-xs">Nombre</Label>
                    <Input
                      value={editingReservation.customer_name || ''}
                      onChange={(e) => setEditingReservation({ ...editingReservation, customer_name: e.target.value })}
                      className="input-gold mt-1"
                      data-testid="edit-customer-name"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Email</Label>
                    <Input
                      value={editingReservation.customer_email || ''}
                      onChange={(e) => setEditingReservation({ ...editingReservation, customer_email: e.target.value })}
                      className="input-gold mt-1"
                      data-testid="edit-customer-email"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-zinc-400 text-xs">Teléfono</Label>
                    <Input
                      value={editingReservation.customer_phone || ''}
                      onChange={(e) => setEditingReservation({ ...editingReservation, customer_phone: e.target.value })}
                      className="input-gold mt-1"
                      data-testid="edit-customer-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Vehicle Info */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-zinc-400">Datos del Vehículo</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-400 text-xs">Matrícula</Label>
                    <Input
                      value={editingReservation.vehicle_plate || ''}
                      onChange={(e) => setEditingReservation({ ...editingReservation, vehicle_plate: e.target.value.toUpperCase() })}
                      className="input-gold mt-1 uppercase"
                      data-testid="edit-vehicle-plate"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Marca</Label>
                    <Input
                      value={editingReservation.vehicle_brand || ''}
                      onChange={(e) => setEditingReservation({ ...editingReservation, vehicle_brand: e.target.value })}
                      className="input-gold mt-1"
                      data-testid="edit-vehicle-brand"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Modelo</Label>
                    <Input
                      value={editingReservation.vehicle_model || ''}
                      onChange={(e) => setEditingReservation({ ...editingReservation, vehicle_model: e.target.value })}
                      className="input-gold mt-1"
                      data-testid="edit-vehicle-model"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Color</Label>
                    <Input
                      value={editingReservation.vehicle_color || ''}
                      onChange={(e) => setEditingReservation({ ...editingReservation, vehicle_color: e.target.value })}
                      className="input-gold mt-1"
                      data-testid="edit-vehicle-color"
                    />
                  </div>
                </div>
              </div>

              {/* Dates and Terminals */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-zinc-400">Fechas y Terminales</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-400 text-xs">Fecha Entrada</Label>
                    <Input
                      type="datetime-local"
                      value={editingReservation.start_dt?.slice(0, 16) || ''}
                      onChange={(e) => setEditingReservation({ ...editingReservation, start_dt: e.target.value })}
                      className="input-gold mt-1"
                      data-testid="edit-start-dt"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Fecha Salida</Label>
                    <Input
                      type="datetime-local"
                      value={editingReservation.end_dt?.slice(0, 16) || ''}
                      onChange={(e) => setEditingReservation({ ...editingReservation, end_dt: e.target.value })}
                      className="input-gold mt-1"
                      data-testid="edit-end-dt"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Terminal Recogida</Label>
                    <Select 
                      value={editingReservation.arrival_terminal || ''} 
                      onValueChange={(v) => setEditingReservation({ ...editingReservation, arrival_terminal: v })}
                    >
                      <SelectTrigger className="input-gold mt-1" data-testid="edit-arrival-terminal">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="T1">Terminal 1</SelectItem>
                        <SelectItem value="T2">Terminal 2</SelectItem>
                        <SelectItem value="T4">Terminal 4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Terminal Devolución</Label>
                    <Select 
                      value={editingReservation.departure_terminal || ''} 
                      onValueChange={(v) => setEditingReservation({ ...editingReservation, departure_terminal: v })}
                    >
                      <SelectTrigger className="input-gold mt-1" data-testid="edit-departure-terminal">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="T1">Terminal 1</SelectItem>
                        <SelectItem value="T2">Terminal 2</SelectItem>
                        <SelectItem value="T4">Terminal 4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Status and Payment */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-zinc-400">Estado y Pago</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-zinc-400 text-xs">Estado</Label>
                    <Select 
                      value={editingReservation.status || ''} 
                      onValueChange={(v) => setEditingReservation({ ...editingReservation, status: v })}
                    >
                      <SelectTrigger className="input-gold mt-1" data-testid="edit-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PENDING_PAYMENT">Pendiente Pago</SelectItem>
                        <SelectItem value="CONFIRMED">Confirmada</SelectItem>
                        <SelectItem value="COMPLETED">Completada</SelectItem>
                        <SelectItem value="CANCELLED">Cancelada</SelectItem>
                        <SelectItem value="NO_SHOW">No Show</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Estado Pago</Label>
                    <Select 
                      value={editingReservation.payment_status || ''} 
                      onValueChange={(v) => setEditingReservation({ ...editingReservation, payment_status: v })}
                    >
                      <SelectTrigger className="input-gold mt-1" data-testid="edit-payment-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PENDING">Pendiente</SelectItem>
                        <SelectItem value="PAID">Pagado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Total (EUR)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={(editingReservation.total_cents / 100).toFixed(2)}
                      onChange={(e) => setEditingReservation({ ...editingReservation, total_cents: Math.round(parseFloat(e.target.value || 0) * 100) })}
                      className="input-gold mt-1"
                      data-testid="edit-total"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-zinc-400 text-xs">Notas</Label>
                <textarea
                  value={editingReservation.notes || ''}
                  onChange={(e) => setEditingReservation({ ...editingReservation, notes: e.target.value })}
                  className="w-full mt-1 p-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm resize-none"
                  rows={3}
                  placeholder="Notas adicionales..."
                  data-testid="edit-notes"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => { setShowEditReservationDialog(false); setEditingReservation(null); }}
                  className="flex-1"
                  data-testid="cancel-edit-reservation"
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSaveReservation}
                  className="flex-1 bg-primary hover:bg-primary/90 text-black"
                  data-testid="save-reservation-btn"
                >
                  Guardar Cambios
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Reservation Confirmation Dialog */}
      <Dialog open={showDeleteReservationDialog} onOpenChange={setShowDeleteReservationDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Eliminar Reserva
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-300 mb-4">
              ¿Estás seguro de que deseas eliminar la reserva <span className="text-primary font-mono font-bold">{reservationToDelete?.reservation_code}</span>?
            </p>
            {reservationToDelete && (
              <div className="bg-zinc-800 rounded p-3 text-sm space-y-1">
                <p className="text-zinc-400">Cliente: <span className="text-white">{reservationToDelete.customer_name}</span></p>
                <p className="text-zinc-400">Matrícula: <span className="text-white font-mono">{reservationToDelete.vehicle_plate}</span></p>
                <p className="text-zinc-400">Fechas: <span className="text-white">{formatDate(reservationToDelete.start_dt)} - {formatDate(reservationToDelete.end_dt)}</span></p>
              </div>
            )}
            <p className="text-destructive/80 text-sm mt-4">
              Esta acción no se puede deshacer. Se guardará un registro de auditoría.
            </p>
            <div className="flex gap-2 mt-6">
              <Button 
                variant="outline" 
                onClick={() => { setShowDeleteReservationDialog(false); setReservationToDelete(null); }}
                className="flex-1"
                data-testid="cancel-delete-reservation"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleDeleteReservation}
                className="flex-1 bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-reservation-btn"
              >
                Eliminar Reserva
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
